import { TypeIcon } from "../common/TypeIcon";
import { Link } from "react-router-dom";
import type { EntityInfo } from "../../hooks/useEntityMap";
import {
  formatStayDay,
  formatStayDuration,
  isStayRunning,
  stayHotel,
  stayHotelName,
  stayNightsLabel,
  type HotelStay,
} from "../../utils/hotelStays";
import { type Zone } from "../../utils/tripDisplay";
import "./Stays.css";

export function StayCard({
  stay,
  hotel,
  tripId,
  zone,
}: {
  stay: HotelStay;
  hotel: EntityInfo | undefined;
  tripId: string;
  zone?: Zone;
}) {
  const name = stayHotelName(stay, hotel);
  const from = formatStayDay(stay.checkIn ?? stay.start, zone);
  const to = isStayRunning(stay) ? "" : formatStayDay(stay.checkOut ?? stay.end, zone);
  return (
    <Link to={`/trips/${tripId}/stays/${stay.id}`} className="stay-card">
      <span className="stay-card-icon" aria-hidden="true">
        {hotel?.imageUrl ? <img src={hotel.imageUrl} alt="" /> : <TypeIcon type="HOTEL" tile />}
      </span>
      <span className="stay-card-text">
        <span className="stay-card-name">{name}</span>
        <span className="stay-card-sub">
          {stayNightsLabel(stay, zone)} · {to ? `${from} – ${to}` : `since ${from}`}
        </span>
        <span className="stay-card-meta">
          {stay.visits.length} {stay.visits.length === 1 ? "visit" : "visits"} · {formatStayDuration(stay.timeAtHotelMs)} there
        </span>
      </span>
      <span className="stay-card-chevron" aria-hidden="true">›</span>
    </Link>
  );
}

/** The trip page's "Stays" list: one card per stay, oldest first. */
export function StaysSection({
  stays,
  entityDataMap,
  tripId,
  zone,
}: {
  stays: HotelStay[];
  entityDataMap: Record<string, EntityInfo | undefined>;
  tripId: string;
  zone?: Zone;
}) {
  if (stays.length === 0) return null;
  return (
    <section className="stays-section">
      <h2 className="stays-title">{stays.length === 1 ? "Stay" : "Stays"}</h2>
      <div className="stays-list">
        {stays.map((stay) => (
          <StayCard key={stay.id} stay={stay} hotel={stayHotel(stay, entityDataMap)} tripId={tripId} zone={zone} />
        ))}
      </div>
    </section>
  );
}
